"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, AlertCircle, RefreshCw, ChevronDown } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { classifyMediaAccessError } from "~/hooks/use-daily-media-errors";
import {
  diagnosticErrorName,
  recordDeviceTestEvent,
  recordMediaOutcome,
  type AttemptReason,
  type DiagnosticPhase,
} from "./diagnostics";

interface CameraCheckProps {
  onPass: () => void;
}

export function CameraCheck({ onPass }: CameraCheckProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const attemptRef = useRef(0);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [error, setError] = useState<{
    type: "permissions" | "not-found" | "in-use" | "unknown";
    message: string;
  } | null>(null);
  const [isStarting, setIsStarting] = useState(true);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(
    async (deviceId?: string, reason: AttemptReason = "initial") => {
      const attempt = ++attemptRef.current;
      const isCurrent = () => attempt === attemptRef.current;
      recordDeviceTestEvent("started", { step: "camera", attempt, reason });
      let phase: DiagnosticPhase = "get_user_media";
      setIsStarting(true);
      setError(null);
      stopStream();

      try {
        const constraints: MediaStreamConstraints = {
          video: deviceId ? { deviceId: { exact: deviceId } } : true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!isCurrent()) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        phase = "enumerate_devices";
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!isCurrent()) return;
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        setCameras(videoDevices);

        const activeTrack = stream.getVideoTracks()[0];
        if (activeTrack) {
          const activeDeviceId = activeTrack.getSettings().deviceId ?? "";
          setSelectedCamera(activeDeviceId);
        }
        void recordMediaOutcome(
          "ready",
          {
            step: "camera",
            attempt,
            track_state: activeTrack?.readyState,
            track_enabled: activeTrack?.enabled,
            track_muted: activeTrack?.muted,
          },
          "camera",
          isCurrent,
        );
      } catch (err) {
        if (!isCurrent()) return;
        const classifiedError = classifyMediaAccessError(err, "camera");
        void recordMediaOutcome(
          "failed",
          {
            step: "camera",
            attempt,
            phase,
            error_name: diagnosticErrorName(err),
            error_type: classifiedError.type,
          },
          "camera",
          isCurrent,
        );
        stopStream();
        setError({
          type:
            classifiedError.type === "device-in-use"
              ? "in-use"
              : classifiedError.type,
          message: classifiedError.helpText ?? classifiedError.message,
        });
      } finally {
        if (isCurrent()) setIsStarting(false);
      }
    },
    [stopStream],
  );

  useEffect(() => {
    void startCamera();
    return () => {
      attemptRef.current += 1;
      stopStream();
    };
  }, [startCamera, stopStream]);

  const handleCameraChange = (deviceId: string) => {
    setSelectedCamera(deviceId);
    void startCamera(deviceId, "device_change");
  };

  return (
    <div className="space-y-4">
      {error ? (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {error.type === "permissions" && "Ne možemo da pristupimo kameri"}
              {error.type === "not-found" && "Kamera nije pronađena"}
              {error.type === "in-use" && "Kamera je zauzeta"}
              {error.type === "unknown" && "Greška sa kamerom"}
            </AlertTitle>
            <AlertDescription className="mt-2">
              {error.message}
            </AlertDescription>
          </Alert>

          <details className="bg-muted/30 rounded-lg border text-sm">
            <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 font-medium">
              <ChevronDown className="h-3.5 w-3.5" />
              Kako da rešim ovaj problem?
            </summary>
            <ul className="text-muted-foreground list-inside list-disc space-y-1.5 px-3 pb-3">
              {error.type === "permissions" && (
                <>
                  <li>Proverite dozvolu za kameru u pregledaču za ovaj sajt</li>
                  <li>Proverite i sistemske dozvole uređaja za pregledač</li>
                  <li>Zatvorite tab i otvorite poziv ponovo nakon promene</li>
                </>
              )}
              {error.type === "not-found" && (
                <>
                  <li>Proverite da li je kamera povezana sa računarom</li>
                  <li>
                    Ako koristite eksternu kameru, iskopčajte je i ponovo
                    povežite
                  </li>
                  <li>
                    Na laptopu proverite da kamera nije isključena tastaturnom
                    prečicom
                  </li>
                </>
              )}
              {error.type === "in-use" && (
                <>
                  <li>
                    Zatvorite druge aplikacije koje koriste kameru (Zoom, Teams,
                    Skype...)
                  </li>
                  <li>
                    Zatvorite druge tabove u pregledaču koji koriste kameru
                  </li>
                  <li>Restartujte pregledač ako problem i dalje postoji</li>
                </>
              )}
              {error.type === "unknown" && (
                <>
                  <li>Osvežite stranicu</li>
                  <li>Pokušajte u drugom pregledaču (Chrome ili Firefox)</li>
                  <li>Restartujte računar ako problem i dalje postoji</li>
                </>
              )}
            </ul>
          </details>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void startCamera(undefined, "retry")}
            className="w-full"
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Pokušaj ponovo
          </Button>
        </div>
      ) : (
        <>
          <div className="ph-no-capture relative aspect-video overflow-hidden rounded-xl bg-gray-900">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full transform-[rotateY(180deg)] object-cover"
            />
            {isStarting && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              </div>
            )}
          </div>

          {cameras.length > 1 && (
            <div className="ph-no-capture">
              <Select value={selectedCamera} onValueChange={handleCameraChange}>
                <SelectTrigger className="ph-no-capture h-9 text-xs">
                  <Camera className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                  <SelectValue placeholder="Kamera" />
                </SelectTrigger>
                <SelectContent className="ph-no-capture">
                  {cameras.map((cam) => (
                    <SelectItem key={cam.deviceId} value={cam.deviceId}>
                      {cam.label || "Kamera"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            onClick={() => {
              recordDeviceTestEvent("step_completed", {
                step: "camera",
                attempt: attemptRef.current,
                method: "manual",
              });
              onPass();
            }}
            className="w-full"
          >
            Vidim se
          </Button>
        </>
      )}
    </div>
  );
}
